import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import emailjs from "@emailjs/browser";
import {
  Home, Warehouse, Factory, Car, Building, Wrench, Hammer, ClipboardList,
  Zap, CalendarDays, CalendarRange,
} from "lucide-react";
import "@/App.css";
import logoUrl from "@/assets/logo.png";
import ref01 from "@/assets/ref-01.jpeg";
import ref02 from "@/assets/ref-02.jpeg";
import ref03 from "@/assets/ref-03.jpeg";
import ref04 from "@/assets/ref-04.jpeg";
import ref05 from "@/assets/ref-05.jpeg";
import ref06 from "@/assets/ref-06.jpeg";
import ref07 from "@/assets/ref-07.jpeg";
import ref08 from "@/assets/ref-08.jpeg";
import ref09 from "@/assets/ref-09.jpeg";

/* ============================== Icons (inline) ============================== */
const I = {
  Roof: (p) => (
    <svg viewBox="0 0 64 64" {...p} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" strokeLinejoin="miter">
      <path d="M4 34 L32 10 L60 34" /><path d="M10 34 L10 54 L54 54 L54 34" />
      <path d="M26 54 L26 38 L38 38 L38 54" />
    </svg>
  ),
  Renovate: (p) => (
    <svg viewBox="0 0 64 64" {...p} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" strokeLinejoin="miter">
      <path d="M6 32 L32 8 L58 32" /><path d="M12 32 L12 56 L52 56 L52 32" />
      <path d="M38 20 L50 32 L42 40 L30 28 Z" /><path d="M30 28 L18 40 L22 48 L30 44" />
    </svg>
  ),
  NewRoof: (p) => (
    <svg viewBox="0 0 64 64" {...p} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" strokeLinejoin="miter">
      <path d="M4 36 L32 8 L60 36" /><path d="M10 36 L10 56 L54 56 L54 36" />
      <path d="M10 42 L54 42" /><path d="M10 48 L54 48" /><path d="M32 8 L32 36" />
    </svg>
  ),
  Insulation: (p) => (
    <svg viewBox="0 0 64 64" {...p} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" strokeLinejoin="miter">
      <path d="M6 34 L32 10 L58 34" /><path d="M12 34 L12 54 L52 54 L52 34" />
      <path d="M18 40 Q24 36 30 40 T42 40 T54 40" />
      <path d="M18 46 Q24 42 30 46 T42 46 T54 46" />
      <path d="M18 52 Q24 48 30 52 T42 52 T54 52" />
    </svg>
  ),
  Tin: (p) => (
    <svg viewBox="0 0 64 64" {...p} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" strokeLinejoin="miter">
      <path d="M8 14 L56 14 L56 50 L8 50 Z" />
      <path d="M14 14 L14 50 M22 14 L22 50 M30 14 L30 50 M38 14 L38 50 M46 14 L46 50" />
    </svg>
  ),
  Star: (p) => <svg viewBox="0 0 24 24" {...p} fill="currentColor"><path d="M12 2 L15 9 L22 9.5 L17 14.5 L18.5 22 L12 18 L5.5 22 L7 14.5 L2 9.5 L9 9 Z" /></svg>,
  Phone: (p) => <svg viewBox="0 0 24 24" {...p} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" /></svg>,
  Mail: (p) => <svg viewBox="0 0 24 24" {...p} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" /><path d="M2 6 L12 14 L22 6" /></svg>,
  Pin: (p) => <svg viewBox="0 0 24 24" {...p} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>,
  Arrow: (p) => <svg viewBox="0 0 24 24" {...p} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" strokeLinejoin="miter"><path d="M5 12 L19 12" /><path d="M13 6 L19 12 L13 18" /></svg>,
  Plus: (p) => <svg viewBox="0 0 24 24" {...p} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5 L12 19" /><path d="M5 12 L19 12" /></svg>,
  Minus: (p) => <svg viewBox="0 0 24 24" {...p} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12 L19 12" /></svg>,
  Close: (p) => <svg viewBox="0 0 24 24" {...p} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M6 6 L18 18 M18 6 L6 18" /></svg>,
  Shield: (p) => <svg viewBox="0 0 24 24" {...p} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 L4 6 L4 12 C4 17 8 21 12 22 C16 21 20 17 20 12 L20 6 Z" /><path d="M9 12 L11 14 L15 10" /></svg>,
  Clock: (p) => <svg viewBox="0 0 24 24" {...p} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 7 L12 12 L15 14" /></svg>,
  Award: (p) => <svg viewBox="0 0 24 24" {...p} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="9" r="6" /><path d="M8 14 L7 22 L12 19 L17 22 L16 14" /></svg>,
  Calc: (p) => <svg viewBox="0 0 24 24" {...p} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="5" y="3" width="14" height="18" /><path d="M8 7 L16 7" /><path d="M8 11 L10 11 M13 11 L16 11" /><path d="M8 15 L10 15 M13 15 L16 15" /><path d="M8 19 L10 19 M13 19 L16 19" /></svg>,
};

/* ============================== Custom hooks ============================== */
const useInView = (threshold = 0.15) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
};

const useCounter = (target, start, duration = 1600) => {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      setV(target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, target, duration]);
  return v;
};

/* ============================== Atoms ============================== */
const Logo = ({ size = "md", onDark = false }) => (
  <span className={`brand brand-${size}`}>
    <span className={`brand-mark ${onDark ? "on-dark" : ""}`}>
      <img src={logoUrl} alt="Takács Endre Tetőfedő logó" />
    </span>
    <span className="brand-text">
      <span className={`brand-name ${onDark ? "light" : ""}`}>Takács Endre</span>
      <span className="brand-tag">TETŐFEDŐ</span>
    </span>
  </span>
);

const Eyebrow = ({ children, center, onDark }) => (
  <div className={`eyebrow ${center ? "center" : ""} ${onDark ? "on-dark" : ""}`}>
    <span className="eyebrow-bar"></span>
    <span>{children}</span>
    <span className="eyebrow-bar eyebrow-bar-right"></span>
  </div>
);

const Reveal = ({ children, delay = 0, as: Tag = "div", className = "", ...rest }) => {
  const [ref, inView] = useInView(0.1);
  return (
    <Tag
      ref={ref}
      className={`reveal ${inView ? "reveal-in" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

/* ============================== Scroll progress ============================== */
const ScrollProgress = () => {
  const [p, setP] = useState(0);
  useEffect(() => {
    const h = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      setP(scrollable > 0 ? (window.scrollY / scrollable) * 100 : 0);
    };
    h();
    window.addEventListener("scroll", h, { passive: true });
    window.addEventListener("resize", h);
    return () => { window.removeEventListener("scroll", h); window.removeEventListener("resize", h); };
  }, []);
  return <div className="scroll-progress"><div style={{ width: `${p}%` }} /></div>;
};

/* ============================== Nav ============================== */
const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);
  const go = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <nav className={`nav ${scrolled ? "nav-scrolled" : ""}`} data-testid="main-nav">
      <div className="nav-inner">
        <a href="#top" onClick={go("top")} data-testid="brand-logo"><Logo size="md" /></a>
        <ul className="nav-links">
          <li><a href="#szolgaltatasok" onClick={go("szolgaltatasok")} data-testid="nav-services">Szolgáltatások</a></li>
          <li><a href="#referenciak" onClick={go("referenciak")} data-testid="nav-references">Referenciák</a></li>
          <li><a href="#rolunk" onClick={go("rolunk")} data-testid="nav-about">Rólunk</a></li>
          <li><a href="#gyik" onClick={go("gyik")} data-testid="nav-faq">GYIK</a></li>
          <li><a href="#kapcsolat" onClick={go("kapcsolat")} data-testid="nav-contact">Kapcsolat</a></li>
        </ul>
        <a href="#kapcsolat" onClick={go("kapcsolat")} className="btn btn-red nav-cta" data-testid="nav-cta-button">
          Ajánlatkérés
        </a>
        <button
          className={`hamburger ${open ? "is-open" : ""}`}
          onClick={() => setOpen(!open)}
          aria-label="Menü"
          data-testid="hamburger-button"
        >
          <span></span><span></span><span></span>
        </button>
      </div>
      <div className={`mobile-menu ${open ? "mobile-menu-open" : ""}`} data-testid="mobile-menu">
        <a href="#szolgaltatasok" onClick={go("szolgaltatasok")}>Szolgáltatások</a>
        <a href="#referenciak" onClick={go("referenciak")}>Referenciák</a>
        <a href="#rolunk" onClick={go("rolunk")}>Rólunk</a>
        <a href="#gyik" onClick={go("gyik")}>GYIK</a>
        <a href="#kapcsolat" onClick={go("kapcsolat")}>Kapcsolat</a>
        <a href="#kapcsolat" onClick={go("kapcsolat")} className="btn btn-red">Ajánlatkérés</a>
      </div>
    </nav>
  );
};

/* ============================== Hero ============================== */
const Hero = () => {
  const go = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <section id="top" className="hero" data-testid="hero-section">
      <div className="hero-bg">
        <div className="hero-photo" aria-hidden="true"></div>
        <div className="hero-overlay" aria-hidden="true"></div>
        <div className="hero-slash" aria-hidden="true"></div>
        <div className="hero-grid" aria-hidden="true"></div>
      </div>

      <div className="hero-content">
        <div className="hero-eyebrow">
          <span className="eyebrow-bar"></span>
          <span>BORSOD-ABAÚJ-ZEMPLÉN &amp; KÖRNYÉKE · 2003 ÓTA</span>
        </div>

        <h1 className="hero-title">
          Megbízható <em>tetőfedés,</em> <br />
          <span className="hero-accent">garanciával.</span>
        </h1>

        <div className="hero-ctas">
          <a href="#kapcsolat" onClick={go("kapcsolat")} className="btn btn-red btn-lg" data-testid="hero-primary-cta">
            Ingyenes felmérés kérése
          </a>
          <a href="#referenciak" onClick={go("referenciak")} className="btn btn-ghost btn-lg" data-testid="hero-secondary-cta">
            Referenciák <I.Arrow className="btn-arrow" />
          </a>
        </div>

        <div className="hero-foot">
          <div className="hero-foot-item">
            <I.Phone className="hero-foot-icon" />
            <a href="tel:+36708033539">+36 70 803 3539</a>
          </div>
          <div className="hero-foot-divider"></div>
          <div className="hero-foot-item">
            <I.Star className="hero-foot-icon hero-foot-icon-star" />
            <span>4.9 / 5 · 180+ Google értékelés</span>
          </div>
          <div className="hero-foot-divider"></div>
          <div className="hero-foot-item">
            <I.Shield className="hero-foot-icon hero-foot-icon-shield" />
            <span>5 év írásos garancia</span>
          </div>
        </div>
      </div>

      <a href="#szolgaltatasok" onClick={go("szolgaltatasok")} className="hero-scroll" aria-label="Görgessen lejjebb">
        <span className="hero-scroll-line"></span>
        <span className="hero-scroll-text">GÖRGESSEN</span>
      </a>
    </section>
  );
};

/* ============================== Trust + Partners ============================== */
const TrustCell = ({ target, suffix, label, visible, decimals = 0 }) => {
  const v = useCounter(target, visible);
  const display = decimals ? v.toFixed(decimals) : Math.round(v).toString();
  return (
    <div className="trust-cell" data-testid={`trust-stat-${label}`}>
      <div className="trust-number">{display}<span className="trust-suffix">{suffix}</span></div>
      <div className="trust-label">{label}</div>
    </div>
  );
};

const TrustBar = () => {
  const [ref, inView] = useInView(0.3);
  return (
    <section ref={ref} className="trust" data-testid="trust-bar">
      <div className="trust-inner">
        <TrustCell target={250} suffix="+" label="elvégzett projekt" visible={inView} />
        <TrustCell target={20} suffix=" év" label="tapasztalat" visible={inView} />
        <TrustCell target={5} suffix=" év" label="garancia" visible={inView} />
        <TrustCell target={4.9} suffix="★" label="Google értékelés" visible={inView} decimals={1} />
      </div>
    </section>
  );
};

const Partners = () => (
  <div className="partners">
    <div className="partners-inner">
      <span className="partners-label">Minősített partnereink:</span>
      <div className="partners-list">
        <span>BRAMAC</span>
        <span>TONDACH</span>
        <span>CREATON</span>
        <span>LINDAB</span>
        <span>VELUX</span>
        <span>ROCKWOOL</span>
      </div>
    </div>
  </div>
);

/* ============================== Services ============================== */
const services = [
  {
    Icon: I.Renovate,
    n: "01",
    title: "Tetőfelújítás",
    sub: "Megmentjük, ami menthető — cseréljük, ami muszáj.",
    text: "Régi tetőszerkezetek teljes vagy részleges felújítása. Cserépcsere, lécezés, fólia és bádogos munkák egy kézből, egy felelősséggel. Nem azt mondjuk, hogy mindent le kell bontani — csak ha tényleg muszáj.",
    img: ref07,
    keywords: ["Cserépcsere", "Lécezés, fóliázás", "Bádogos munkák", "Kéménykörnyék"],
    specs: [
      { label: "Ár / m²", value: "12 000–22 000 Ft" },
      { label: "Tipikus idő", value: "5–14 nap" },
      { label: "Garancia", value: "5 év írásban" },
    ],
  },
  {
    Icon: I.NewRoof,
    n: "02",
    title: "Új tetőfedés",
    sub: "Új építésnél ácsmunkától az utolsó kúpcserépig.",
    text: "Új építésű házak teljes tetőszerkezete — ácsmunka, lécezés, fóliázás, fedés, bádogos kiegészítők. Bramac, Tondach, Creaton, Lindab — csak minősített, garanciás anyagokkal dolgozunk.",
    img: ref08,
    keywords: ["Ácsmunka", "Cserépfedés", "Lemez- és zsindelyfedés", "Kiegészítő elemek"],
    specs: [
      { label: "Ár / m²", value: "18 000–28 000 Ft" },
      { label: "Tipikus idő", value: "2–3 hét" },
      { label: "Anyaggarancia", value: "akár 30 év" },
    ],
  },
  {
    Icon: I.Insulation,
    n: "03",
    title: "Hőszigetelés",
    sub: "Akár 40% fűtési költségmegtakarítás — kimutathatóan.",
    text: "Tetőtér és padlásfödém szigetelése kőzetgyapottal vagy üveggyapottal. Páraáteresztő- és párazáró fólia, akusztikai szigetelés. Olyan rétegrenddel, ami télen meleget tart, nyáron hűvöset.",
    img: ref06,
    keywords: ["Tetőtér", "Padlásfödém", "Párazárás", "Akusztika"],
    specs: [
      { label: "Ár / m²", value: "6 000–11 000 Ft" },
      { label: "Megtérülés", value: "3–5 év" },
      { label: "Anyagvastagság", value: "20–30 cm" },
    ],
  },
  {
    Icon: I.Tin,
    n: "04",
    title: "Bádogos munkák",
    sub: "A részlet, ami megvédi az egészet az időjárástól.",
    text: "Ereszcsatornák, lefolyók, kéményszegélyek, oromdeszkák — titáncink, horganyzott acél, rézlemez kivitelben. A bádogos munka az, ami egy tetőt 5 évig vagy 30 évig szárazon tart.",
    img: ref09,
    keywords: ["Ereszcsatornák", "Lefolyócsövek", "Kéményszegély", "Oromdeszka"],
    specs: [
      { label: "Ár / fm", value: "8 000–14 000 Ft" },
      { label: "Tipikus idő", value: "1–3 nap" },
      { label: "Anyagok", value: "Ti-Zn / horg. / réz" },
    ],
  },
];

const Services = () => {
  const goContact = (e) => {
    e.preventDefault();
    document.getElementById("kapcsolat")?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <section id="szolgaltatasok" className="srv" data-testid="services-section">
      <div className="srv-head">
        <div className="srv-head-left">
          <Eyebrow>AMIVEL FOGLALKOZUNK</Eyebrow>
          <h2 className="section-title">Négy szakág, <br /><span className="text-red">egy mester.</span></h2>
        </div>
        <div className="srv-head-right">
          <p className="srv-head-text">
            Minden, ami a tetőhöz tartozik — egy csapattól, egy árajánlatban,
            egy felelősséggel. Nem közvetítünk kifelé, nem hárítunk.
          </p>
          <div className="srv-head-meta">
            <span className="srv-head-meta-num">04</span>
            <span className="srv-head-meta-text">SZAKÁG</span>
          </div>
        </div>
      </div>

      <div className="srv-rows">
        {services.map((s, i) => (
          <Reveal
            key={s.title}
            delay={i * 80}
            as="article"
            className={`srv-row ${i % 2 === 1 ? "srv-row-rev" : ""}`}
            data-testid={`service-card-${i}`}
          >
            <div className="srv-visual">
              <span className="srv-visual-num" aria-hidden="true">{s.n}</span>
              <div className="srv-image">
                <img src={s.img} alt={s.title} loading="lazy" />
              </div>
              <div className="srv-stamp">
                <span>SZOLGÁLTATÁS · {s.n}</span>
              </div>
            </div>

            <div className="srv-content">
              <div className="srv-content-head">
                <s.Icon className="srv-icon" />
                <span className="srv-divider"></span>
                <span className="srv-counter">{s.n} <em>/ 04</em></span>
              </div>

              <h3 className="srv-title">{s.title}</h3>
              <p className="srv-text">{s.text}</p>

              <div className="srv-specs">
                {s.specs.map((sp) => (
                  <div className="srv-spec" key={sp.label}>
                    <div className="srv-spec-label">{sp.label}</div>
                    <div className="srv-spec-value">{sp.value}</div>
                  </div>
                ))}
              </div>

              <div className="srv-footrow">
                <div className="srv-keywords">
                  {s.keywords.map((k) => (
                    <span key={k} className="srv-kw">{k}</span>
                  ))}
                </div>
                <a href="#kapcsolat" className="srv-cta" onClick={goContact} data-testid={`service-cta-${i}`}>
                  <span>Ajánlatot kérek</span>
                  <I.Arrow className="srv-cta-arrow" />
                </a>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

/* ============================== Calculator ============================== */
const CALC_TYPES = [
  { id: "uj", label: "Új tetőfedés", min: 18000, max: 28000 },
  { id: "felujitas", label: "Tetőfelújítás", min: 12000, max: 22000 },
  { id: "badogos", label: "Bádogos munka", min: 8000, max: 14000 },
  { id: "szigeteles", label: "Hőszigetelés", min: 6000, max: 11000 },
];
const CALC_MATERIALS = [
  { id: "cserep", label: "Cserép (Tondach / Bramac)", factor: 1.0 },
  { id: "lemez", label: "Lemez / trapézlemez", factor: 0.82 },
  { id: "zsindely", label: "Bitumen zsindely", factor: 0.9 },
];
const CALC_COMPLEXITY = [
  { id: "egyszeru", label: "Egyszerű (sátor, nyereg)", factor: 1.0 },
  { id: "osszetett", label: "Összetett (kontyolt)", factor: 1.12 },
  { id: "bonyolult", label: "Bonyolult (torony, manzárd)", factor: 1.28 },
];

const fmt = (n) => Math.round(n).toLocaleString("hu-HU").replace(/,/g, " ");

const Calculator = () => {
  const [type, setType] = useState("uj");
  const [material, setMaterial] = useState("cserep");
  const [complexity, setComplexity] = useState("egyszeru");
  const [area, setArea] = useState(120);

  const result = useMemo(() => {
    const t = CALC_TYPES.find((x) => x.id === type);
    const m = CALC_MATERIALS.find((x) => x.id === material);
    const c = CALC_COMPLEXITY.find((x) => x.id === complexity);
    const f = (m?.factor || 1) * (c?.factor || 1);
    const min = (t?.min || 0) * f * area;
    const max = (t?.max || 0) * f * area;
    return { min, max };
  }, [type, material, complexity, area]);

  const scrollContact = (e) => {
    e.preventDefault();
    const el = document.getElementById("kapcsolat");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="arkalkulator" className="calc" data-testid="calculator-section">
      <div className="calc-inner">
        <div className="calc-head">
          <Eyebrow onDark>AZONNALI BECSLÉS</Eyebrow>
          <h2 className="section-title section-title-light">Árkalkulátor</h2>
          <p className="section-sub section-sub-light">
            Adja meg a projekt paramétereit, és azonnal tájékoztató jellegű árat kap.
            A <strong>végleges ajánlat</strong> minden esetben helyszíni felmérés után készül.
          </p>
        </div>

        <div className="calc-grid">
          <div className="calc-form">
            <div className="calc-field">
              <label className="calc-label">Munka jellege</label>
              <div className="calc-options">
                {CALC_TYPES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className={`calc-chip ${type === t.id ? "is-active" : ""}`}
                    onClick={() => setType(t.id)}
                    data-testid={`calc-type-${t.id}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="calc-field">
              <label className="calc-label">Fedőanyag</label>
              <div className="calc-options">
                {CALC_MATERIALS.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    className={`calc-chip ${material === m.id ? "is-active" : ""}`}
                    onClick={() => setMaterial(m.id)}
                    data-testid={`calc-material-${m.id}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="calc-field">
              <label className="calc-label">Tetőforma</label>
              <div className="calc-options">
                {CALC_COMPLEXITY.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className={`calc-chip ${complexity === c.id ? "is-active" : ""}`}
                    onClick={() => setComplexity(c.id)}
                    data-testid={`calc-complexity-${c.id}`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="calc-field">
              <div className="calc-label-row">
                <label className="calc-label" htmlFor="calc-area">Alapterület</label>
                <span className="calc-area-value">{area} m²</span>
              </div>
              <input
                id="calc-area"
                type="range"
                min="30"
                max="500"
                step="5"
                value={area}
                onChange={(e) => setArea(Number(e.target.value))}
                className="calc-range"
                data-testid="calc-area-input"
              />
              <div className="calc-range-ticks">
                <span>30 m²</span><span>150 m²</span><span>300 m²</span><span>500 m²</span>
              </div>
            </div>
          </div>

          <aside className="calc-result" data-testid="calc-result">
            <div className="calc-result-tag">
              <I.Calc className="calc-result-icon" />
              <span>BECSÜLT ÁRKATEGÓRIA</span>
            </div>
            <div className="calc-result-range">
              <div className="calc-result-from">
                <span className="calc-result-ft">{fmt(result.min)} Ft</span>
              </div>
              <div className="calc-result-sep">–</div>
              <div className="calc-result-to">
                <span className="calc-result-ft">{fmt(result.max)} Ft</span>
              </div>
            </div>
            <div className="calc-result-note">
              Bruttó, nagyságrendi becslés. Tartalmaz anyagot és munkadíjat<br />
              a kiválasztott kategória átlagos alapáraival.
            </div>

            <ul className="calc-incl">
              <li><I.Shield className="calc-incl-ico" /> 5 év írásos garancia</li>
              <li><I.Clock className="calc-incl-ico" /> Pontos ajánlat 48 órán belül</li>
              <li><I.Award className="calc-incl-ico" /> Eredeti, minősített anyagok</li>
            </ul>

            <a href="#kapcsolat" onClick={scrollContact} className="btn btn-red btn-lg calc-cta" data-testid="calc-cta-button">
              Kérem a pontos ajánlatot
            </a>
          </aside>
        </div>
      </div>
    </section>
  );
};

/* ============================== Process ============================== */
const steps = [
  { n: 1, title: "Felmérés", text: "Kiszállunk Önhöz, átnézzük a tetőt, meghallgatjuk az igényeit." },
  { n: 2, title: "Ajánlat (48h)", text: "48 órán belül részletes, tételes írásos ajánlatot kap." },
  { n: 3, title: "Kivitelezés", text: "Egyeztetett ütemezéssel, tiszta munkaterülettel dolgozunk." },
  { n: 4, title: "Átadás (garancia)", text: "Átadjuk a munkát, megkapja az 5 éves írásos garanciát." },
];

const Process = () => (
  <section className="process" data-testid="process-section">
    <div className="section-head">
      <Eyebrow onDark>ÍGY DOLGOZUNK</Eyebrow>
      <h2 className="section-title section-title-light">Négy lépés a kész tetőig</h2>
      <p className="section-sub section-sub-light">
        Átlátható folyamat, tiszta kommunikáció. Nincsenek rejtett költségek, nincsenek meglepetések.
      </p>
    </div>

    <div className="process-track">
      <div className="process-line" aria-hidden="true"></div>
      {steps.map((s, i) => (
        <Reveal key={s.n} delay={i * 120} className="process-step" data-testid={`process-step-${s.n}`}>
          <div className="process-circle">
            <span className="process-num">{s.n}</span>
          </div>
          <h4 className="process-title">{s.title}</h4>
          <p className="process-text">{s.text}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ============================== About ============================== */
const milestones = [
  { year: "2003", text: "Tanulóként édesapám mellett kezdtem a szakmát." },
  { year: "2008", text: "Saját vállalkozást indítottam Mezőkövesden." },
  { year: "2015", text: "Csapattá nőttünk – ma 5-en dolgozunk együtt." },
  { year: "2024", text: "250+ projekt, 4.9★ Google értékelés." },
];

const About = () => (
  <section id="rolunk" className="abt" data-testid="about-section">
    <div className="abt-inner">

      <div className="abt-head">
        <Eyebrow>RÓLUNK</Eyebrow>
        <h2 className="abt-title">
          A mester, aki maga <br />
          is <span className="text-red">felmegy a tetőre.</span>
        </h2>
        <div className="abt-meta">
          <span className="abt-meta-label">B.-A.-Z. MEGYE</span>
          <span className="abt-meta-dot"></span>
          <span className="abt-meta-label">2003 ÓTA</span>
          <span className="abt-meta-dot"></span>
          <span className="abt-meta-label">5 FŐS CSAPAT</span>
        </div>
      </div>

      <div className="abt-pull">
        <span className="abt-pull-mark" aria-hidden="true">"</span>
        <p className="abt-pull-text">
          Egy tető nem heti munka — <em>30 évre szóló döntés</em>.<br />
          Ezért dolgozom úgy, mintha a sajátom lenne.
        </p>
      </div>

      <div className="abt-body">
        <aside className="abt-portrait" data-testid="about-portrait">
          <div className="abt-portrait-frame">
            <img
              src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=900&q=80"
              alt="Takács Endre – tetőfedő mester"
              loading="lazy"
            />
            <div className="abt-portrait-tape abt-portrait-tape-tl"></div>
            <div className="abt-portrait-tape abt-portrait-tape-br"></div>
          </div>
          <div className="abt-portrait-cap">
            <div>
              <strong>Takács Endre</strong>
              <span>tetőfedő mester</span>
            </div>
            <span className="abt-portrait-year">EST. 2003</span>
          </div>
        </aside>

        <div className="abt-text">
          <p className="abt-lead">
            <span className="abt-dropcap">2</span>003-ban édesapám mellett kezdtem a
            szakmát — lécet hordani, cserepet adogatni. Ő tanított meg arra, hogy
            <strong> egy tetőn nem lehet két és fél munkát végezni:</strong> vagy
            szárazon tartja a házat 30 évig, vagy nem.
          </p>

          <p className="abt-para">
            Azóta én is mester vagyok, és saját csapatot építettem fel. Nem
            közvetítünk kifelé, nem hárítunk. Ami nálunk áll, az meg is marad — és
            ha mégsem, visszajövünk és megcsináljuk. Ezt nevezem garanciának.
          </p>

          <p className="abt-para">
            Nem vagyunk a legolcsóbbak. Cserébe a határidőinket egy napra pontosan
            tartjuk, az ajánlatainkban nincsenek apró betűs csapdák, és a
            munkaterületet úgy hagyjuk, ahogy találtuk — csak már új tetővel.
          </p>

          <div className="abt-sign">
            <div className="abt-sign-name">Takács Endre</div>
            <div className="abt-sign-role">— tetőfedő mester, Mezőkövesd</div>
          </div>
        </div>
      </div>

      <div className="abt-timeline" data-testid="about-timeline">
        <div className="abt-timeline-head">
          <span className="abt-timeline-label">UTUNK</span>
          <span className="abt-timeline-line"></span>
          <span className="abt-timeline-label abt-timeline-label-right">22 ÉV</span>
        </div>
        <div className="abt-timeline-grid">
          {milestones.map((m, i) => (
            <div key={m.year} className="abt-mile" data-testid={`milestone-${i}`}>
              <div className="abt-mile-year">{m.year}</div>
              <div className="abt-mile-dot" aria-hidden="true"></div>
              <div className="abt-mile-text">{m.text}</div>
            </div>
          ))}
        </div>
      </div>

    </div>
  </section>
);

/* ============================== References ============================== */
const refData = [
  { tag: "Új tetőfedés cseréppel", loc: "Mezőkövesd", year: "2024", img: ref02, desc: "Teljes új tetőszerkezet, kontyolt kialakítás Tondach cseréppel, kéménybekötések bádogos munkával." },
  { tag: "Cserepes tetőfedés", loc: "Eger", year: "2024", img: ref01, desc: "Klasszikus piros égetett cserép precíz fektetéssel. Egyenletes kúpsor, tiszta vonalvezetés." },
  { tag: "Lemezfedés családi házon", loc: "Miskolc", year: "2023", img: ref03, desc: "Lindab lemezfedés, kéménybekötések és komplett bádogos munkák — ereszcsatornával." },
  { tag: "Cserepes lemezfedés", loc: "Mezőkeresztes", year: "2023", img: ref04, desc: "Hullámos cserepes lemezfedés régi családi házon, új gerinccel és teljes ereszcsatorna rendszerrel." },
  { tag: "Új cseréptető + bádogos", loc: "Tard", year: "2023", img: ref05, desc: "Antracit színű cseréptető garázzsal, körbefutó ereszcsatornával és precíz párkányokkal." },
];

const BeforeAfter = () => {
  const [pct, setPct] = useState(50);
  const boxRef = useRef(null);
  const dragging = useRef(false);

  const update = useCallback((clientX) => {
    const box = boxRef.current;
    if (!box) return;
    const rect = box.getBoundingClientRect();
    const p = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
    setPct(p);
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      update(x);
    };
    const onUp = () => { dragging.current = false; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [update]);

  const start = (e) => {
    dragging.current = true;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    update(x);
  };

  return (
    <div className="ba" ref={boxRef} onMouseDown={start} onTouchStart={start} data-testid="before-after">
      <img
        className="ba-img ba-after"
        src="https://images.unsplash.com/photo-1605276374104-dee2a0ed3cd6?auto=format&fit=crop&w=1400&q=80"
        alt="Felújítás után"
        draggable="false"
      />
      <div className="ba-clip" style={{ clipPath: `inset(0 ${100 - pct}% 0 0)` }}>
        <img
          className="ba-img"
          src="https://images.unsplash.com/photo-1632759145351-1d592919f522?auto=format&fit=crop&w=1400&q=80"
          alt="Felújítás előtt"
          draggable="false"
        />
      </div>
      <div className="ba-handle" style={{ left: `${pct}%` }}>
        <div className="ba-handle-line"></div>
        <div className="ba-handle-knob">
          <I.Arrow className="ba-handle-arrow ba-handle-arrow-l" />
          <I.Arrow className="ba-handle-arrow ba-handle-arrow-r" />
        </div>
      </div>
      <div className="ba-label ba-label-before">ELŐTTE</div>
      <div className="ba-label ba-label-after">UTÁNA</div>
    </div>
  );
};

const Lightbox = ({ item, onClose, onPrev, onNext }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose, onPrev, onNext]);
  if (!item) return null;
  return (
    <div className="lightbox" onClick={onClose} data-testid="lightbox">
      <button className="lb-close" onClick={onClose} aria-label="Bezárás" data-testid="lightbox-close">
        <I.Close />
      </button>
      <button className="lb-prev" onClick={(e) => { e.stopPropagation(); onPrev(); }} aria-label="Előző">‹</button>
      <button className="lb-next" onClick={(e) => { e.stopPropagation(); onNext(); }} aria-label="Következő">›</button>
      <div className="lb-box" onClick={(e) => e.stopPropagation()}>
        <img src={item.img} alt={item.tag} />
        <div className="lb-meta">
          <div className="lb-tag">{item.tag}</div>
          <div className="lb-loc"><I.Pin className="lb-ico" /> {item.loc} · {item.year}</div>
          <p className="lb-desc">{item.desc}</p>
        </div>
      </div>
    </div>
  );
};

const References = () => {
  const [idx, setIdx] = useState(null);
  const open = (i) => setIdx(i);
  const close = () => setIdx(null);
  const prev = () => setIdx((i) => (i === null ? 0 : (i - 1 + refData.length) % refData.length));
  const next = () => setIdx((i) => (i === null ? 0 : (i + 1) % refData.length));

  return (
    <section id="referenciak" className="refs" data-testid="references-section">
      <div className="section-head">
        <Eyebrow>MUNKÁINK</Eyebrow>
        <h2 className="section-title">Referenciák</h2>
        <p className="section-sub">
          Néhány projekt az elmúlt évekből. Mindegyik mögött egy elégedett tulajdonos áll.
          Kattintson egy fotóra a nagyításhoz.
        </p>
      </div>

      <div className="refs-grid">
        {refData.map((r, i) => (
          <Reveal key={i} delay={i * 70} as="figure" className="ref-card" data-testid={`reference-${i}`}>
            <button className="ref-image" onClick={() => open(i)} aria-label={`${r.tag} — nagyítás`}>
              <img src={r.img} alt={`${r.tag} – ${r.loc}`} loading="lazy" />
              <div className="ref-overlay">
                <span className="ref-year">{r.year}</span>
                <span className="ref-zoom">Nagyítás +</span>
              </div>
            </button>
            <figcaption className="ref-caption">
              <span className="ref-tag">{r.tag}</span>
              <span className="ref-loc"><I.Pin className="ref-pin" /> {r.loc}</span>
            </figcaption>
          </Reveal>
        ))}
      </div>

      {idx !== null && (
        <Lightbox item={refData[idx]} onClose={close} onPrev={prev} onNext={next} />
      )}
    </section>
  );
};

/* ============================== Testimonials (letters) ============================== */
const reviews = [
  {
    quote: "Endre és a csapata egy hétvége alatt újracserepezték a pajtát. Pontosan érkeztek, tisztán dolgoztak, mindent úgy hagytak, ahogy találták – csak már vízhatlan tetővel. Az ajánlat és a végszámla forintra megegyezett.",
    name: "Kovács Péter", loc: "Mezőkövesd", date: "2024. szeptember", project: "Cserépcsere · 120 m²", featured: true,
  },
  {
    quote: "Három ajánlat közül Takács úrét választottuk, és nem bántuk meg. Új tetőt kaptunk a családi házunkra, a határidőt egy napra pontosan tartották. Öt év garanciát írásban adtak – ez ma ritka.",
    name: "Szabó Anita", loc: "Eger", date: "2024. június", project: "Új tetőfedés · 186 m²",
  },
  {
    quote: "A régi ereszcsatorna teljesen tönkrement. Másnap kijöttek felmérni, egy héten belül kész is volt az új titáncink rendszer. Amit ígértek, azt tartották, és még a kertet is leseperték.",
    name: "Nagy László", loc: "Miskolc", date: "2023. november", project: "Bádogos munka",
  },
];

const Testimonials = () => (
  <section className="vlm" data-testid="testimonials-section">
    <div className="vlm-inner">
      <div className="vlm-head">
        <div className="vlm-head-left">
          <Eyebrow>VÉLEMÉNYEK</Eyebrow>
          <h2 className="section-title">
            Mit írtak <br /><span className="text-red">rólunk.</span>
          </h2>
        </div>
        <div className="vlm-head-right">
          <div className="vlm-rating">
            <div className="vlm-rating-num">4.9</div>
            <div className="vlm-rating-info">
              <div className="vlm-rating-stars" aria-label="4.9 csillag">
                {[0, 1, 2, 3, 4].map((k) => <I.Star key={k} className="vlm-rating-star" />)}
              </div>
              <div className="vlm-rating-text">
                <strong>180+ értékelés</strong>
                <span>a Google-on</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="vlm-grid">
        {reviews.map((r, i) => (
          <Reveal
            key={i}
            delay={i * 100}
            as="article"
            className={`vlm-card ${r.featured ? "vlm-card-feat" : ""}`}
            data-testid={`testimonial-${i}`}
          >
            <div className="vlm-paper">
              <div className="vlm-tape" aria-hidden="true"></div>
              <div className="vlm-meta-top">
                <div className="vlm-stars" aria-label="5 csillag">
                  {[0, 1, 2, 3, 4].map((k) => <I.Star key={k} className="vlm-star" />)}
                </div>
                <span className="vlm-project">{r.project}</span>
              </div>
              <p className="vlm-quote">
                <span className="vlm-quote-mark" aria-hidden="true">"</span>
                {r.quote}
              </p>
              <div className="vlm-foot">
                <div className="vlm-sig">{r.name}</div>
                <div className="vlm-meta">{r.loc} · {r.date}</div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ============================== FAQ (magazine Q&A) ============================== */
const faqs = [
  { q: "Mennyibe kerül egy tetőfedés négyzetmétere?", a: "Az új tetőfedés 18 000–28 000 Ft/m², a felújítás 12 000–22 000 Ft/m² nagyságrendben mozog, de ez nagyban függ a választott anyagtól, a tetőformától és a kiegészítő munkáktól. A végleges árat minden esetben helyszíni felmérés után adjuk." },
  { q: "Milyen hosszú garanciát adnak?", a: "Minden munkánkra 5 év írásos kivitelezési garanciát vállalunk, a beépített anyagokra pedig a gyártói garancia érvényes (Tondach, Bramac cserepekre akár 30 év). A garanciát szerződésben rögzítjük." },
  { q: "Mennyi ideig tart egy átlagos munka?", a: "Egy 150 m²-es családi ház új tetőszerkezete kb. 2–3 hét. Egy cserépcsere 3–7 nap, egy bádogos munka 1–3 nap. Pontos ütemezést a felmérés után, az ajánlatban adunk." },
  { q: "Dolgoznak télen is?", a: "Igen, de csak olyan munkákat vállalunk, amik télen szakszerűen elvégezhetők — bádogos, belső szigetelés, kisebb javítások. Nagyobb cserépfedést −5 °C alatt nem végzünk, mert az a minőség rovására menne." },
  { q: "Milyen területen dolgoznak?", a: "Elsősorban Borsod-Abaúj-Zemplén megyében — Mezőkövesd központtal 60 km-es körzetben. Nagyobb projekteknél távolabb is vállalunk munkát, ezt előzetesen egyeztetjük." },
  { q: "Kell-e előleget fizetni?", a: "Csak az anyagköltség 30–40%-át kérjük előlegként, a nagyértékű anyag megrendeléséhez. A munkadíjat a kivitelezés végén, átadáskor, a minőség ellenőrzése után kell kifizetni." },
];

const FAQ = () => (
  <section id="gyik" className="qa" data-testid="faq-section">
    <div className="qa-inner">
      <div className="qa-head">
        <div className="qa-head-left">
          <Eyebrow>GYAKORI KÉRDÉSEK</Eyebrow>
          <h2 className="section-title">
            Mielőtt felhív, <br /><span className="text-red">olvassa el.</span>
          </h2>
        </div>
        <div className="qa-head-right">
          <p className="qa-head-sub">
            A leggyakoribb kérdések — őszinte, kerek mondatos válaszokkal. Ha valami
            mégis kimaradna, hívjon bizalommal, szívesen elmondjuk személyesen is.
          </p>
        </div>
      </div>

      <div className="qa-grid">
        {faqs.map((f, i) => (
          <Reveal key={i} delay={i * 60} as="article" className="qa-item" data-testid={`faq-item-${i}`}>
            <div className="qa-num">{String(i + 1).padStart(2, "0")}</div>
            <div className="qa-content">
              <div className="qa-row">
                <span className="qa-label">K.</span>
                <h3 className="qa-q">{f.q}</h3>
              </div>
              <div className="qa-row qa-row-a">
                <span className="qa-label qa-label-a">V.</span>
                <p className="qa-a">{f.a}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="qa-cta">
        <div className="qa-cta-text">Nem talált választ a kérdésére?</div>
        <a href="tel:+36708033539" className="qa-cta-call" data-testid="faq-call-link">
          <I.Phone className="qa-cta-icon" />
          <span className="qa-cta-num">+36 70 803 3539</span>
          <I.Arrow className="qa-cta-arrow" />
        </a>
      </div>
    </div>
  </section>
);

/* ============================== Survey (7-step, aurumroof-stílusban) ============================== */
const surveySteps = [
  {
    id: "area",
    q: "Melyik leírás illik legjobban a munkaterülethez?",
    type: "options",
    options: [
      { id: "csaladi", Icon: Home, label: "Családi ház" },
      { id: "eloteto", Icon: Warehouse, label: "Előtető" },
      { id: "ipari", Icon: Factory, label: "Ipari épület" },
      { id: "kocsibeallo", Icon: Car, label: "Kocsibeálló" },
      { id: "egyeb", Icon: Building, label: "Egyéb…" },
    ],
  },
  {
    id: "work",
    q: "Melyik leírás illik legjobban a munka jellegéhez?",
    type: "options",
    options: [
      { id: "tetofedes", Icon: Home, label: "Tetőfedés" },
      { id: "tetojavitas", Icon: Wrench, label: "Tetőjavítás" },
      { id: "badogozas", Icon: Hammer, label: "Bádogozás" },
      { id: "egyeb-munka", Icon: ClipboardList, label: "Egyéb…" },
    ],
  },
  {
    id: "size",
    q: "Mekkora az épület hozzávetőleges alapterülete?",
    type: "options",
    options: [
      { id: "s", badge: "S", label: "Kicsi: 50 m² alatt" },
      { id: "m", badge: "M", label: "Közepes: 51–100 m²" },
      { id: "l", badge: "L", label: "Nagy: 101–200 m²" },
      { id: "xl", badge: "XL", label: "Hatalmas: 201 m² felett" },
    ],
  },
  {
    id: "when",
    q: "Mikor tervezi a munka kezdetét?",
    type: "options",
    options: [
      { id: "azonnal", Icon: Zap, label: "Azonnal, vagy 1–2 héten belül szeretném elkezdeni" },
      { id: "het24", Icon: CalendarDays, label: "2–4 héten belül" },
      { id: "ho12", Icon: CalendarRange, label: "1–2 hónapon belül" },
    ],
  },
  {
    id: "name",
    q: "Hogyan szólíthatjuk?",
    type: "form",
    fields: [
      { name: "vezeteknev", label: "Vezetéknév", required: true, placeholder: "Kovács" },
      { name: "keresztnev", label: "Keresztnév", required: true, placeholder: "János" },
    ],
  },
  {
    id: "contact",
    q: "Hol vehetjük fel önnel a kapcsolatot?",
    type: "form",
    fields: [
      { name: "email", label: "Email", required: true, type: "email", placeholder: "email@example.com" },
      { name: "phone", label: "Telefonszám", required: true, type: "tel", placeholder: "+36 70 123 4567" },
    ],
  },
  {
    id: "address",
    q: "Hol található a munka helyszíne?",
    type: "form",
    isLast: true,
    fields: [
      { name: "iranyitoszam", label: "Irányítószám", required: true, placeholder: "1234", colSpan: 1 },
      { name: "telepules", label: "Település", required: true, placeholder: "Budapest", colSpan: 1 },
      { name: "utca", label: "Utca / Házszám", required: true, placeholder: "Fő utca 1.", colSpan: 2 },
    ],
  },
];

const optionLabel = (sid, value) => {
  const step = surveySteps.find((s) => s.id === sid);
  if (!step || !step.options) return value;
  const opt = step.options.find((o) => o.id === value);
  return opt ? opt.label : value;
};

/* EmailJS config */
const EMAILJS_SERVICE_ID = "service_20j1qxg";
const EMAILJS_TEMPLATE_ID = "template_oj10g5d";
const EMAILJS_PUBLIC_KEY = "4ZAXLZpFXiXAsd6Im";

const Survey = ({ openPrivacy, topVariant }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [form, setForm] = useState({});
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const current = surveySteps[step];
  const total = surveySteps.length;
  const pct = Math.round(((step + 1) / total) * 100);

  const choose = (optId) => {
    setAnswers((a) => ({ ...a, [current.id]: optId }));
    if (step < total - 1) setStep(step + 1);
  };
  const back = () => step > 0 && setStep(step - 1);

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const formStepValid = () => {
    if (current.type !== "form") return false;
    return current.fields.every((f) => !f.required || (form[f.name] || "").trim());
  };

  const next = () => {
    if (!formStepValid()) return;
    if (current.isLast) {
      sendEmail();
    } else {
      setStep(step + 1);
    }
  };

  const sendEmail = async () => {
    setSending(true);
    setError("");
    const payload = {
      vezeteknev: form.vezeteknev || "",
      keresztnev: form.keresztnev || "",
      email: form.email || "",
      phone: form.phone || "",
      iranyitoszam: form.iranyitoszam || "",
      telepules: form.telepules || "",
      utca: form.utca || "",
      area_label: optionLabel("area", answers.area) || "—",
      work_label: optionLabel("work", answers.work) || "—",
      size_label: optionLabel("size", answers.size) || "—",
      when_label: optionLabel("when", answers.when) || "—",
      timestamp: new Date().toLocaleString("hu-HU", { timeZone: "Europe/Budapest" }),
    };
    try {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, payload, { publicKey: EMAILJS_PUBLIC_KEY });
      setSent(true);
    } catch (err) {
      console.error("EmailJS error:", err);
      setError("Sajnos nem sikerült elküldeni az igénylést. Kérjük hívjon minket közvetlenül: +36 70 803 3539");
    } finally {
      setSending(false);
    }
  };

  const restart = () => {
    setStep(0);
    setAnswers({});
    setForm({});
    setSent(false);
    setError("");
  };

  return (
    <section
      id={topVariant ? undefined : "kapcsolat"}
      className={`srvy ${topVariant ? "srvy-top" : ""}`}
      data-testid={topVariant ? "top-survey-section" : "cta-section"}
    >
      <div className="srvy-bg" aria-hidden="true"></div>
      <div className="srvy-inner">
        <div className="srvy-head">
          <Eyebrow onDark center>GYORS ÁRAJÁNLAT</Eyebrow>
          <h2 className="srvy-title">Kérjen ingyenes <span className="text-red">szakmai felmérést</span></h2>
          <p className="srvy-sub">
            Néhány kérdés, és 48 órán belül visszahívjuk a kért időpontban —
            személyre szabott árajánlattal. Nem kerül semmibe.
          </p>
        </div>

        {!sent ? (
          <div className="srvy-card" data-testid="survey-card">
            <div className="srvy-progress">
              <div className="srvy-progress-meta">
                <span className="srvy-progress-step">Lépés {step + 1} / {total}</span>
                <span className="srvy-progress-pct">{pct}%</span>
              </div>
              <div className="srvy-progress-bar">
                <div className="srvy-progress-fill" style={{ width: `${pct}%` }}></div>
              </div>
            </div>

            <div className="srvy-q-wrap" key={step}>
              <h3 className="srvy-q" data-testid={`survey-question-${step}`}>{current.q}</h3>

              {current.type === "options" && (
                <>
                  <div className={`srvy-options ${current.id === "size" ? "srvy-options-2" : ""}`}>
                    {current.options.map((o) => {
                      const active = answers[current.id] === o.id;
                      const Ic = o.Icon;
                      return (
                        <button
                          key={o.id}
                          type="button"
                          className={`srvy-opt ${active ? "is-active" : ""}`}
                          onClick={() => choose(o.id)}
                          data-testid={`survey-opt-${current.id}-${o.id}`}
                        >
                          {o.badge ? (
                            <span className="srvy-opt-badge">{o.badge}</span>
                          ) : (
                            Ic && <Ic className="srvy-opt-icon" strokeWidth={1.6} />
                          )}
                          <span className="srvy-opt-label">
                            <strong>{o.label}</strong>
                          </span>
                          <span className="srvy-opt-arrow"><I.Arrow /></span>
                        </button>
                      );
                    })}
                  </div>

                  <div className="srvy-actions">
                    {step > 0 ? (
                      <button type="button" className="srvy-back" onClick={back} data-testid="survey-back">
                        ← Vissza
                      </button>
                    ) : <span />}
                    <span className="srvy-hint">Kattintson egy lehetőségre a továbblépéshez</span>
                  </div>
                </>
              )}

              {current.type === "form" && (
                <form
                  className="srvy-contact"
                  onSubmit={(e) => { e.preventDefault(); next(); }}
                  data-testid={`survey-form-${current.id}`}
                >
                  <div className="srvy-fields">
                    {current.fields.map((f) => (
                      <div
                        key={f.name}
                        className={`srvy-field ${f.colSpan === 2 ? "srvy-field-full" : ""}`}
                      >
                        <label>{f.label} {f.required && "*"}</label>
                        <input
                          type={f.type || "text"}
                          required={f.required}
                          value={form[f.name] || ""}
                          onChange={(e) => setField(f.name, e.target.value)}
                          placeholder={f.placeholder}
                          data-testid={`survey-input-${f.name}`}
                        />
                      </div>
                    ))}
                  </div>

                  {current.isLast && (
                    <div className="srvy-consent">
                      A kérdőív kitöltésével elfogadja az{" "}
                      <button type="button" className="srvy-consent-link" onClick={openPrivacy} data-testid="survey-consent-link">
                        adatvédelmi tájékoztatóban
                      </button>{" "}
                      leírtakat.
                    </div>
                  )}

                  {error && (
                    <div className="srvy-error" data-testid="survey-error">{error}</div>
                  )}

                  <div className="srvy-actions">
                    <button type="button" className="srvy-back" onClick={back} disabled={sending} data-testid="survey-back">
                      ← Vissza
                    </button>
                    <button type="submit" className="btn btn-red btn-lg srvy-submit" disabled={sending} data-testid="survey-next">
                      {current.isLast ? (sending ? "Küldés…" : "Beküldés") : "Tovább →"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        ) : (
          <div className="srvy-done" data-testid="survey-done">
            <div className="srvy-done-mark"><I.Shield /></div>
            <h3>Köszönjük, {(form.keresztnev || "").trim() || "kedves Érdeklődő"}!</h3>
            <p>
              Megkaptuk a kérdőívet. <strong>48 órán belül</strong> felvesszük Önnel a kapcsolatot
              a megadott elérhetőségeken, és személyre szabott ajánlatot küldünk.
            </p>
            <div className="srvy-done-info">
              {form.phone && <div><span>Telefonszám</span><strong>{form.phone}</strong></div>}
              {form.email && <div><span>Email</span><strong>{form.email}</strong></div>}
              {form.telepules && <div><span>Helyszín</span><strong>{form.telepules}</strong></div>}
            </div>
            <div className="srvy-done-summary">
              <div className="srvy-summary-label">ÖSSZEFOGLALÓ</div>
              <div className="srvy-summary-grid">
                <div><span>Munkaterület</span><strong>{optionLabel("area", answers.area) || "—"}</strong></div>
                <div><span>Munka jellege</span><strong>{optionLabel("work", answers.work) || "—"}</strong></div>
                <div><span>Méret</span><strong>{optionLabel("size", answers.size) || "—"}</strong></div>
                <div><span>Időzítés</span><strong>{optionLabel("when", answers.when) || "—"}</strong></div>
              </div>
            </div>
            <button type="button" className="srvy-back" onClick={restart}>
              ← Új igénylés indítása
            </button>
          </div>
        )}

        <div className="srvy-divider"><span>vagy hívjon most</span></div>

        <a href="tel:+36708033539" className="cta-phone" data-testid="cta-phone-link">
          +36 70 803 3539
        </a>

        <div className="cta-badges">
          <div className="cta-badge"><I.Star className="cta-badge-icon" /> 4.9 Google</div>
          <div className="cta-badge"><I.Clock className="cta-badge-icon" /> 48 óra válaszidő</div>
          <div className="cta-badge"><I.Shield className="cta-badge-icon" /> 5 év garancia</div>
        </div>
      </div>
    </section>
  );
};

const CTASection = Survey;

/* ============================== Legal modal (Privacy & Cookie) ============================== */
const LegalContext = { setModal: null };

const LegalModal = ({ type, onClose }) => {
  useEffect(() => {
    if (!type) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [type, onClose]);

  if (!type) return null;

  const privacyContent = (
    <>
      <div className="legal-meta">Hatályos: 2025 · Weboldal: takacsendretetofedo.hu</div>

      <h3>1. Adatkezelő adatai</h3>
      <div className="legal-card">
        <div><span>Név</span><strong>Takács Endre tetőfedő</strong></div>
        <div><span>Adószám</span><strong>91769468-1-25</strong></div>
        <div><span>Székhely</span><strong>3400 Mezőkövesd, Gergely utca 8</strong></div>
        <div><span>Telefon</span><strong><a href="tel:+36708033539">+36 70 803 3539</a></strong></div>
        <div><span>Email</span><strong><a href="mailto:tetoepito1@gmail.com">tetoepito1@gmail.com</a></strong></div>
      </div>

      <h3>2. Kezelt adatok köre</h3>
      <p>A weboldal használata során az alábbi adatokat kezeljük:</p>
      <ul>
        <li>Név</li>
        <li>Telefonszám</li>
        <li>Email cím</li>
        <li>Üzenet tartalma</li>
        <li>IP cím</li>
        <li>Böngészési adatok (cookie-k révén)</li>
      </ul>

      <h3>3. Az adatkezelés célja</h3>
      <ul>
        <li>Kapcsolatfelvétel biztosítása</li>
        <li>Ajánlatadás</li>
        <li>Ügyfélkommunikáció</li>
        <li>Weboldal működésének biztosítása</li>
        <li>Felhasználói élmény javítása</li>
      </ul>

      <h3>4. Az adatkezelés jogalapja</h3>
      <ul>
        <li>Az érintett hozzájárulása</li>
        <li>Szerződés teljesítése</li>
        <li>Jogszabályi kötelezettség</li>
      </ul>

      <h3>5. Az adatok tárolásának időtartama</h3>
      <ul>
        <li><strong>Kapcsolatfelvételi adatok:</strong> legfeljebb 1 év</li>
        <li><strong>Számlázási adatok:</strong> 8 év (jogszabály alapján)</li>
        <li><strong>Cookie-k:</strong> böngészőtől függően</li>
      </ul>

      <h3>6. Adattovábbítás</h3>
      <p>Az adatokat harmadik félnek nem adjuk át, kivéve:</p>
      <ul>
        <li>Könyvelő</li>
        <li>Hatóságok jogszabályi kötelezettség esetén</li>
      </ul>

      <h3>7. Az érintettek jogai</h3>
      <p>Az érintett jogosult:</p>
      <ul>
        <li>Tájékoztatást kérni az adatairól</li>
        <li>Helyesbítést kérni</li>
        <li>Törlést kérni</li>
        <li>Adatkezelés korlátozását kérni</li>
        <li>Tiltakozni az adatkezelés ellen</li>
      </ul>
      <p className="legal-note">
        <strong>Kérelem esetén:</strong> <a href="mailto:tetoepito1@gmail.com">tetoepito1@gmail.com</a>
      </p>

      <h3>8. Adatbiztonság</h3>
      <p>
        Az adatkezelő megfelelő technikai és szervezési intézkedéseket alkalmaz
        az adatok védelme érdekében.
      </p>
    </>
  );

  const cookieContent = (
    <>
      <div className="legal-meta">Hatályos: 2025 · Weboldal: takacsendretetofedo.hu</div>

      <h3>1. Mik azok a cookie-k?</h3>
      <p>
        A cookie-k kis adatfájlok, amelyeket a böngésző tárol a felhasználó eszközén.
      </p>

      <h3>2. Használt cookie-k típusai</h3>
      <div className="legal-list-box">
        <div>
          <strong>Alapműködéshez szükséges cookie-k</strong>
          <span>Weboldal működéséhez elengedhetetlenek.</span>
        </div>
        <div>
          <strong>Statisztikai cookie-k</strong>
          <span>Látogatói viselkedés elemzése.</span>
        </div>
        <div>
          <strong>Marketing cookie-k</strong>
          <span>Hirdetések személyre szabása (opcionális).</span>
        </div>
      </div>

      <h3>3. Cookie-k kezelése</h3>
      <p>
        A felhasználó a böngésző beállításaiban bármikor törölheti vagy
        letilthatja a cookie-kat.
      </p>

      <h3>4. Harmadik fél cookie-k</h3>
      <p>A weboldal használhat:</p>
      <ul>
        <li>Google Analytics (statisztika)</li>
        <li>Google Ads (hirdetések)</li>
      </ul>

      <h3>5. Hozzájárulás</h3>
      <p>
        A weboldal használatával a látogató elfogadja a cookie-k használatát.
      </p>

      <div className="legal-divider"></div>

      <h3>Záró rész</h3>
      <p>Fenntartjuk a jogot a tájékoztató módosítására.</p>
    </>
  );

  const isPrivacy = type === "privacy";
  const title = isPrivacy ? "Adatvédelmi tájékoztató" : "Cookie tájékoztató";
  const eyebrow = isPrivacy ? "JOGI DOKUMENTUM" : "COOKIE HASZNÁLAT";

  return (
    <div className="legal-overlay" onClick={onClose} data-testid={`legal-modal-${type}`}>
      <div className="legal-dialog" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <button className="legal-close" onClick={onClose} aria-label="Bezárás" data-testid="legal-close">
          <I.Close />
        </button>
        <div className="legal-head">
          <span className="legal-eyebrow">{eyebrow}</span>
          <h2 className="legal-title">{title}</h2>
        </div>
        <div className="legal-body">
          {isPrivacy ? privacyContent : cookieContent}
        </div>
        <div className="legal-foot">
          <button className="btn btn-red" onClick={onClose} data-testid="legal-accept">
            Rendben, megértettem
          </button>
        </div>
      </div>
    </div>
  );
};

/* ============================== Cookie banner ============================== */
const CookieBanner = ({ openPrivacy, openCookie }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    try {
      if (!localStorage.getItem("te_cookie_ok")) {
        const t = setTimeout(() => setVisible(true), 600);
        return () => clearTimeout(t);
      }
    } catch { /* ignore */ }
  }, []);
  const accept = () => {
    try { localStorage.setItem("te_cookie_ok", "1"); } catch { /* ignore */ }
    setVisible(false);
  };
  if (!visible) return null;
  return (
    <div className="cookie" data-testid="cookie-banner">
      <div className="cookie-inner">
        <div className="cookie-text">
          <strong>Sütiket használunk</strong>
          <span>
            A weboldal működtetéséhez és a felhasználói élmény javítása érdekében
            sütiket használunk. A további használattal elfogadja ezek használatát.
            Részletek az {" "}
            <button type="button" className="cookie-link" onClick={openPrivacy}>Adatvédelmi</button>{" "}
            és {" "}
            <button type="button" className="cookie-link" onClick={openCookie}>Cookie tájékoztatóban</button>.
          </span>
        </div>
        <button className="btn btn-red cookie-btn" onClick={accept} data-testid="cookie-accept">
          Rendben
        </button>
      </div>
    </div>
  );
};

/* ============================== Footer ============================== */
const Footer = ({ openPrivacy, openCookie }) => (
  <footer className="footer" data-testid="footer">
    <div className="footer-inner">
      <div className="footer-col footer-brand-col">
        <Logo size="md" onDark />
        <p className="footer-tag">
          Tetőfedés, ácsmunka, bádogos munkák és hőszigetelés
          Borsod-Abaúj-Zemplén megyében – 2003 óta.
        </p>
        <div className="footer-social">
          <a href="#" aria-label="Facebook" className="footer-soc" onClick={(e) => e.preventDefault()}>f</a>
          <a href="#" aria-label="Instagram" className="footer-soc" onClick={(e) => e.preventDefault()}>ig</a>
          <a href="#" aria-label="Google" className="footer-soc" onClick={(e) => e.preventDefault()}>G</a>
        </div>
      </div>

      <div className="footer-col">
        <h5 className="footer-h">Szolgáltatások</h5>
        <ul className="footer-list footer-list-plain">
          <li><a href="#szolgaltatasok">Tetőfelújítás</a></li>
          <li><a href="#szolgaltatasok">Új tetőfedés</a></li>
          <li><a href="#szolgaltatasok">Bádogos munkák</a></li>
          <li><a href="#szolgaltatasok">Hőszigetelés</a></li>
          <li><a href="#kapcsolat">Ajánlatkérés</a></li>
        </ul>
      </div>

      <div className="footer-col">
        <h5 className="footer-h">Elérhetőség</h5>
        <ul className="footer-list">
          <li><I.Phone className="footer-ico" /> <a href="tel:+36708033539">+36 70 803 3539</a></li>
          <li><I.Mail className="footer-ico" /> <a href="mailto:tetoepito1@gmail.com">tetoepito1@gmail.com</a></li>
          <li><I.Pin className="footer-ico" /> 3400 Mezőkövesd, Gergely utca 8</li>
        </ul>
        <div className="footer-hours">
          <strong>H–P:</strong> 7:00–18:00 &nbsp;·&nbsp; <strong>Szo:</strong> 8:00–14:00
        </div>
      </div>

      <div className="footer-col">
        <h5 className="footer-h">Jogi információk</h5>
        <ul className="footer-list footer-list-plain">
          <li><button type="button" className="footer-link-btn" onClick={openPrivacy} data-testid="footer-privacy">Adatvédelmi tájékoztató</button></li>
          <li><button type="button" className="footer-link-btn" onClick={openCookie} data-testid="footer-cookie">Cookie tájékoztató</button></li>
          <li><button type="button" className="footer-link-btn" onClick={openPrivacy}>Impresszum</button></li>
        </ul>
      </div>
    </div>

    <div className="footer-bottom">
      <span>© {new Date().getFullYear()} Takács Endre Tetőfedő · Minden jog fenntartva.</span>
      <span className="footer-adoszam">Adószám: 91769468-1-25</span>
    </div>
  </footer>
);

/* ============================== Floating Call ============================== */
const FloatingCall = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const h = () => setShow(window.scrollY > 600);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);
  return (
    <a
      href="tel:+36708033539"
      className={`floating-call ${show ? "is-visible" : ""}`}
      aria-label="Hívás most"
      data-testid="floating-call-button"
    >
      <I.Phone className="floating-call-icon" />
      <span>Hívás</span>
    </a>
  );
};

/* ============================== App ============================== */
export default function App() {
  const [legal, setLegal] = useState(null); // 'privacy' | 'cookie' | null
  const openPrivacy = () => setLegal("privacy");
  const openCookie = () => setLegal("cookie");
  const closeLegal = () => setLegal(null);

  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";
  }, []);

  return (
    <div className="landing" data-testid="landing-root">
      <ScrollProgress />
      <Nav />
      <Hero />
      <Survey openPrivacy={openPrivacy} topVariant />
      <TrustBar />
      <Services />
      <Process />
      <About />
      <References />
      <FAQ />
      <Testimonials />
      <CTASection openPrivacy={openPrivacy} />
      <Footer openPrivacy={openPrivacy} openCookie={openCookie} />
      <FloatingCall />
      <LegalModal type={legal} onClose={closeLegal} />
      <CookieBanner openPrivacy={openPrivacy} openCookie={openCookie} />
    </div>
  );
}
